import { useState } from 'react';

import { Trash2, Plus, ChevronDown, ChevronRight, Filter, GripVertical } from 'lucide-react';

import { FormInput, FormSelect, FormTextarea } from '@/components/form';
import { Button } from '@/components/ui/button';

export interface ParameterDefinition {
  name: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description: string;
  required: boolean;
  enum?: string[];
  isFixed?: boolean;
  fixedValue?: unknown;
  in?: 'path' | 'query' | 'body' | 'filter' | 'display';
  path_variable?: string;
  query_param?: string;
  filter_config?: {
    target_field: string;
    operator: '>=' | '<=' | '>' | '<' | '==' | '!=' | 'contains' | 'starts_with';
  };
  items?: {
    type: 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array';
    properties?: ParameterDefinition[];
    required?: string[];
  };
  properties?: ParameterDefinition[];
}

const JSON_SCHEMA_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
] as const;

const PARAMETER_IN_OPTIONS = [
  { value: 'body', label: 'Body' },
  { value: 'query', label: 'Query' },
  { value: 'path', label: 'Path' },
  { value: 'filter', label: 'Filter' },
  // Not sent to the API: when the AI sets it to true, the customer doesn't see the result.
  { value: 'display', label: 'Display control' },
] as const;

const FILTER_OPERATOR_OPTIONS = [
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '==', label: '==' },
  { value: '!=', label: '!=' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
] as const;

interface ParameterEditorProps {
  parameter: ParameterDefinition;
  onChange: (param: ParameterDefinition) => void;
  onRemove: () => void;
  level?: number;
  pathVariables?: string[];
  availableFilterFields?: string[];
  autoExpand?: boolean;
}

export const ParameterEditor = ({
  parameter,
  onChange,
  onRemove,
  level = 0,
  pathVariables = [],
  availableFilterFields = [],
  autoExpand = false,
}: ParameterEditorProps) => {
  const [isExpanded, setIsExpanded] = useState(autoExpand || level > 0);

  const updateField = <K extends keyof ParameterDefinition>(
    field: K,
    value: ParameterDefinition[K],
  ) => {
    onChange({ ...parameter, [field]: value });
  };

  const addNestedProperty = () => {
    const newProp: ParameterDefinition = {
      name: '',
      type: 'string',
      description: '',
      required: false,
    };

    if (parameter.type === 'array') {
      const currentItems = parameter.items || { type: 'object', properties: [] };
      const currentProps = currentItems.properties || [];
      onChange({
        ...parameter,
        items: {
          ...currentItems,
          type: 'object',
          properties: [...currentProps, newProp],
        },
      });
    } else if (parameter.type === 'object') {
      const currentProps = parameter.properties || [];
      onChange({
        ...parameter,
        properties: [...currentProps, newProp],
      });
    }
  };

  const removeNestedProperty = (index: number) => {
    if (parameter.type === 'array' && parameter.items?.properties) {
      const updatedProps = parameter.items.properties.filter((_, i) => i !== index);
      onChange({
        ...parameter,
        items: { ...parameter.items, properties: updatedProps },
      });
    } else if (parameter.type === 'object' && parameter.properties) {
      onChange({
        ...parameter,
        properties: parameter.properties.filter((_, i) => i !== index),
      });
    }
  };

  const updateNestedProperty = (index: number, updatedProp: ParameterDefinition) => {
    if (parameter.type === 'array' && parameter.items?.properties) {
      const updatedProps = [...parameter.items.properties];
      updatedProps[index] = updatedProp;
      onChange({
        ...parameter,
        items: { ...parameter.items, properties: updatedProps },
      });
    } else if (parameter.type === 'object' && parameter.properties) {
      const updatedProps = [...parameter.properties];
      updatedProps[index] = updatedProp;
      onChange({ ...parameter, properties: updatedProps });
    }
  };

  const hasNesting = parameter.type === 'array' || parameter.type === 'object';
  const nestedProperties =
    parameter.type === 'array' ? parameter.items?.properties : parameter.properties;
  const isFilter = parameter.in === 'filter';

  const indentClass = level > 0 ? 'ml-4 border-l-2 border-blue-200 pl-3' : '';

  // Compact summary line for collapsed state
  const summaryLabel = [
    parameter.name || '(unnamed)',
    parameter.type,
    parameter.in || 'body',
    parameter.required ? 'required' : '',
    parameter.isFixed ? 'fixed' : '',
    isFilter && parameter.filter_config?.target_field
      ? `→ ${parameter.filter_config.target_field} ${parameter.filter_config.operator}`
      : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={`${indentClass}`}>
      <div
        className={`rounded-lg border ${isFilter ? 'border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/20' : 'bg-muted/30'}`}
      >
        {/* Compact header row — always visible */}
        <div
          className="flex cursor-pointer items-center gap-2 px-3 py-2"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <GripVertical size={14} className="text-muted-foreground shrink-0" />
          {isFilter && <Filter size={14} className="shrink-0 text-blue-500" />}
          <button type="button" className="shrink-0">
            {isExpanded ? (
              <ChevronDown size={16} className="text-muted-foreground" />
            ) : (
              <ChevronRight size={16} className="text-muted-foreground" />
            )}
          </button>
          <span className="text-muted-foreground min-w-0 flex-1 truncate text-sm">
            {summaryLabel}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="text-destructive hover:text-destructive h-7 w-7 shrink-0 p-0"
          >
            <Trash2 size={14} />
          </Button>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="space-y-3 border-t px-3 pt-3 pb-3">
            {/* Row 1: Name + Type + Location + Required */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <FormInput
                placeholder="Parameter name"
                value={parameter.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="text-sm"
              />
              <FormSelect
                value={parameter.type}
                onChange={(e) => {
                  const newType = e.target.value as ParameterDefinition['type'];
                  const updates: Partial<ParameterDefinition> = { type: newType };
                  if (newType === 'array') {
                    updates.items = { type: 'object', properties: [] };
                  } else if (newType === 'object') {
                    updates.properties = [];
                  } else {
                    updates.items = undefined;
                    updates.properties = undefined;
                  }
                  onChange({ ...parameter, ...updates });
                }}
              >
                {JSON_SCHEMA_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </FormSelect>
              <FormSelect
                value={parameter.in || 'body'}
                onChange={(e) => {
                  const newIn = e.target.value as ParameterDefinition['in'];
                  const updates: Partial<ParameterDefinition> = { in: newIn };
                  if (newIn === 'filter') {
                    updates.filter_config = { target_field: '', operator: '>=' };
                    updates.isFixed = undefined;
                    updates.fixedValue = undefined;
                  } else {
                    updates.filter_config = undefined;
                  }
                  onChange({ ...parameter, ...updates });
                }}
              >
                {PARAMETER_IN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </FormSelect>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={parameter.required}
                    onChange={(e) => updateField('required', e.target.checked)}
                    className="rounded"
                    disabled={parameter.isFixed}
                  />
                  <span className="text-xs">Required</span>
                </label>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={parameter.isFixed || false}
                    onChange={(e) => updateField('isFixed', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs">Fixed</span>
                </label>
              </div>
            </div>

            {/* Row 2: Description */}
            <FormTextarea
              placeholder="Description for AI (what this parameter is for)"
              value={parameter.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={1}
              className="text-sm"
            />

            {/* Conditional sections — inline, not stacked */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {/* Enum values */}
              {(parameter.type === 'string' || parameter.type === 'number') && (
                <FormInput
                  placeholder="Enum values (comma-separated)"
                  value={parameter.enum?.join(', ') || ''}
                  onChange={(e) => {
                    const values = e.target.value
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean);
                    updateField('enum', values.length > 0 ? values : undefined);
                  }}
                  className="text-sm"
                />
              )}

              {/* Fixed value input */}
              {parameter.isFixed && (
                <FormInput
                  placeholder="Fixed value"
                  value={
                    parameter.fixedValue !== undefined ? JSON.stringify(parameter.fixedValue) : ''
                  }
                  onChange={(e) => {
                    try {
                      updateField('fixedValue', JSON.parse(e.target.value));
                    } catch {
                      updateField('fixedValue', e.target.value);
                    }
                  }}
                  className="text-sm"
                />
              )}

              {/* Path variable mapping */}
              {parameter.in === 'path' && pathVariables.length > 0 && (
                <FormSelect
                  value={parameter.path_variable || ''}
                  onChange={(e) => updateField('path_variable', e.target.value || undefined)}
                  className="text-sm"
                >
                  <option value="">Map to path variable...</option>
                  {pathVariables.map((v) => (
                    <option key={v} value={v}>
                      {`{${v}}`}
                    </option>
                  ))}
                </FormSelect>
              )}

              {/* Query param name mapping — real query-string name when it
                  differs from the (valid-identifier) property key. */}
              {parameter.in === 'query' && (
                <FormInput
                  placeholder="Query param name (if different from key), e.g. max-price"
                  value={parameter.query_param || ''}
                  onChange={(e) => updateField('query_param', e.target.value || undefined)}
                  className="text-sm"
                />
              )}
            </div>

            {/* Filter config — compact inline */}
            {isFilter && (
              <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50/50 p-2 dark:border-blue-800 dark:bg-blue-950/30">
                <span className="text-xs font-medium whitespace-nowrap">Filter:</span>
                {availableFilterFields.length > 0 ? (
                  <FormSelect
                    value={parameter.filter_config?.target_field || ''}
                    onChange={(e) =>
                      onChange({
                        ...parameter,
                        filter_config: {
                          ...parameter.filter_config!,
                          target_field: e.target.value,
                        },
                      })
                    }
                    className="text-sm"
                  >
                    <option value="">Select field...</option>
                    {availableFilterFields.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </FormSelect>
                ) : (
                  <FormInput
                    placeholder="target field"
                    value={parameter.filter_config?.target_field || ''}
                    onChange={(e) =>
                      onChange({
                        ...parameter,
                        filter_config: {
                          ...parameter.filter_config!,
                          target_field: e.target.value,
                        },
                      })
                    }
                    className="max-w-32 text-sm"
                  />
                )}
                <FormSelect
                  value={parameter.filter_config?.operator || '>='}
                  onChange={(e) =>
                    onChange({
                      ...parameter,
                      filter_config: {
                        ...parameter.filter_config!,
                        operator: e.target.value as NonNullable<
                          ParameterDefinition['filter_config']
                        >['operator'],
                      },
                    })
                  }
                  className="max-w-28 text-sm"
                >
                  {FILTER_OPERATOR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </FormSelect>
                <span className="text-muted-foreground text-xs">AI value</span>
              </div>
            )}

            {/* Nested properties */}
            {hasNesting && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium">
                    {parameter.type === 'array' ? 'Array Item Properties' : 'Object Properties'}
                  </label>
                  <Button type="button" variant="outline" size="sm" onClick={addNestedProperty}>
                    <Plus size={14} className="mr-1" />
                    Add
                  </Button>
                </div>
                {nestedProperties && nestedProperties.length > 0 ? (
                  <div className="space-y-1">
                    {nestedProperties.map((nestedProp, index) => (
                      <ParameterEditor
                        key={index}
                        parameter={nestedProp}
                        onChange={(updated) => updateNestedProperty(index, updated)}
                        onRemove={() => removeNestedProperty(index)}
                        level={level + 1}
                        pathVariables={pathVariables}
                        availableFilterFields={availableFilterFields}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground py-1 text-xs italic">No properties yet.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
